'use client';

import { useMemo } from 'react';
import { Container, Grid, GridCol, Text, Title } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useViewportSize } from '@mantine/hooks';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { renderOptions } from '@lib/renderOptions';
import Image from 'next/image';
import classes from './ImageCarousel.module.scss';

function resolveSlideAsset(slide) {
    if (!slide || !slide.fields) {
        return null;
    }

    if (slide.fields.file?.url) {
        return {
            asset: slide,
            altText: slide.fields.altText,
        };
    }

    if (slide.fields.image?.fields?.file?.url) {
        return {
            asset: slide.fields.image,
            altText: slide.fields.altText,
        };
    }

    if (slide.fields.asset?.fields?.file?.url) {
        return {
            asset: slide.fields.asset,
            altText: slide.fields.altText,
        };
    }

    return null;
}

function renderDescription(description) {
    if (!description) {
        return null;
    }

    if (typeof description === 'string') {
        return <Text mt="sm">{description}</Text>;
    }

    return (
        <div className="content-area">
            {documentToReactComponents(description, renderOptions)}
        </div>
    );
}

export function ImageCarousel({ data }) {
    const headingLevel = data.headingLevel ? parseInt(data.headingLevel.charAt(1), 10) : 2;
    const { width } = useViewportSize();

    const sourceSlides = data.slides || data.images || data.items || [];
    const slides = sourceSlides.map(resolveSlideAsset).filter(Boolean);
    const slidesPerPage = useMemo(() => {
        if (width > 992) {
            return 3;
        }

        if (width > 768) {
            return 2;
        }

        return 1;
    }, [width]);

    const slideSize = useMemo(() => {
        if (slidesPerPage === 3) {
            return '33.3333%';
        }

        if (slidesPerPage === 2) {
            return '50%';
        }

        return '100%';
    }, [slidesPerPage]);

    const totalPages = useMemo(() => {
        return Math.max(1, Math.ceil(slides.length / slidesPerPage));
    }, [slides.length, slidesPerPage]);

    if (!slides.length) {
        return null;
    }

    return (
        <section className={classes.carouselSection}>
            {(data.title || data.description) && (
                <Container size="lg">
                    <Grid>
                        <GridCol span={{ lg: 11 }}>
                            {data.title && <Title order={headingLevel}>{data.title}</Title>}
                            {renderDescription(data.description)}
                        </GridCol>
                    </Grid>
                </Container>
            )}

            <Container size="lg" mt={data.title || data.description ? 'lg' : 0}>
                <Carousel
                    key={`carousel-${slidesPerPage}`}
                    className={classes.carousel}
                    slideSize={slideSize}
                    slideGap={0}
                    align="start"
                    withIndicators={totalPages > 1}
                    withControls={totalPages > 1}
                    emblaOptions={{ loop: false, dragFree: false }}
                    classNames={{
                        viewport: classes.viewport,
                        controls: classes.controls,
                        control: classes.control,
                        indicators: classes.indicators,
                        indicator: classes.indicator,
                    }}
                >
                    {slides.map((slide, index) => {
                        const file = slide.asset.fields.file;
                        const imageUrl = `https:${file.url}?w=720&fm=avif`;
                        const altText = slide.altText || slide.asset.fields.description || slide.asset.fields.title || `Carousel slide ${index + 1}`;
                        const imageWidth = file.details.image?.width || 720;
                        const imageHeight = file.details.image?.height || 405;

                        return (
                            <Carousel.Slide key={`${slide.asset.sys?.id || 'slide'}-${index}`} className={classes.slide}>
                                <Image src={imageUrl} alt={altText} width={imageWidth} height={imageHeight} className={classes.image} loading="lazy" />
                            </Carousel.Slide>
                        );
                    })}
                </Carousel>
            </Container>
        </section>
    );
}
